<template>
  <b-modal
    :id="'modal-view-store-order' + orderId"
    size="xl"
    centered
    scrollable
    :title="'Store Order #' + orderId"
    content-class="pos-modal"
    dialog-class="pos-dialog"
    @hidden="$emit('closed')"
  >
    <div class="order-view">

      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted">Loading order details…</p>
      </div>

      <div v-else-if="fetchError" class="alert-view" style="border-color: #F3C9C2; background: #FBE9E7;">
        <b-icon icon="exclamation-triangle-fill" style="color: #C93C30;"></b-icon>
        <strong>Could not load order:</strong> {{ fetchError }}
        <b-button size="sm" class="vs-btn-ghost ml-3" @click="loadAll">Retry</b-button>
      </div>

      <div v-else-if="header">
        <section class="vs-card" style="margin-bottom: 14px;">
          <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 14px;">
            <div class="d-flex" style="gap: 12px; align-items: center;">
              <span class="vs-icon"><b-icon icon="receipt"></b-icon></span>
              <div>
                <h3 style="margin: 0; font-family: 'Sora', sans-serif; font-weight: 700;">
                  Order #{{ header.id }}
                </h3>
                <p style="margin: 0; color: #736C63; font-size: 13px;">
                  {{ getLocName(header.ordering_location) }}
                  <b-icon icon="arrow-left"></b-icon>
                  {{ getLocName(header.selling_location) }}
                </p>
                <p style="margin: 0; color: #736C63; font-size: 12px;">
                  Created by <b>{{ header.created_by || '—' }}</b>
                  · {{ formatDate(header.ts) }}
                </p>
              </div>
            </div>

            <div class="d-flex align-items-center" style="gap: 10px;">
              <span class="vs-chip" :class="getStatusTagClass(header.status)">
                {{ getStatusText(header.status) }}
              </span>

              <b-button
                v-if="isAdminOrManager"
                class="vs-btn-primary"
                :disabled="approving || header.approved || !canApprove"
                @click="approveOrder"
                v-b-tooltip.hover
                :title="header.approved ? 'Already approved' : (!canApprove ? 'Set Request ETA first' : 'Approve order')"
              >
                <b-spinner small v-if="approving"></b-spinner>
                <b-icon v-else icon="shield-check"></b-icon>
                {{ header.approved ? 'Approved' : 'Approve' }}
              </b-button>
            </div>
          </div>
        </section>

        <b-tabs v-model="activeTab" card class="order-view-tabs">

          <!-- ================= PRODUCT DETAILS ================= -->
          <b-tab title="Product Details" :title-link-class="'otab is-product'">
            <div style="padding: 12px;">
              <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 10px;">
                <span class="vs-count">
                  {{ items.length }} line{{ items.length === 1 ? '' : 's' }} · {{ sumQty('quantity') }} units
                </span>
                <div class="d-flex align-items-center" style="gap: 8px;">

                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="loadAllAvailability"
                    :disabled="isLoadingAllAvailability || saving"
                  >
                    <b-spinner small v-if="isLoadingAllAvailability"></b-spinner>
                    <b-icon v-else icon="arrow-repeat"></b-icon>
                    Load All Availability
                  </b-button>

                  <b-form-select
                    v-if="isAdmin"
                    v-model="bulkStatus"
                    :options="bulkStatusOptions"
                    size="sm"
                    style="width: 180px;"
                    :disabled="saving"
                    @change="applyBulkStatus"
                  >
                    <template #first>
                      <option value="">Bulk Set Status…</option>
                    </template>
                  </b-form-select>

                  <b-button v-if="hasChanges" class="vs-btn-ghost" @click="resetItems" :disabled="saving">
                    <b-icon icon="x"></b-icon> Reset
                  </b-button>
                  <b-button class="vs-btn-primary" :disabled="!hasChanges || saving || !canSave" @click="saveItems">
                    <b-spinner small v-if="saving"></b-spinner>
                    <b-icon v-else icon="save"></b-icon>
                    {{ saving ? 'Saving…' : 'Save Changes' }}
                  </b-button>
                </div>
              </div>

              <div class="ord-product-list">
                <article
                  v-for="(item, idx) in items"
                  :key="'row-' + idx"
                  class="ord-product-card"
                  :class="'is-status-' + item.status"
                >
                  <!-- IMAGE -->
                  <div class="ord-product-img">
                    <input
                      type="file" accept="image/*"
                      :ref="'fileInput-' + idx"
                      style="display:none"
                      @change="handleImageUpload($event, idx)"
                    />
                    <img
                      v-if="item.images && item.images.length"
                      :src="item.images[0].raw || item.images[0].url"
                      @click="$refs['fileInput-' + idx][0].click()"
                      style="cursor:pointer;"
                    />
                    <div v-else class="ord-product-img-empty" @click="$refs['fileInput-' + idx][0].click()">
                      <b-icon icon="camera" font-scale="2"></b-icon>
                      <span>Upload</span>
                    </div>
                    <b-button
                      v-if="item.images && item.images.length"
                      size="sm" variant="danger"
                      class="ord-product-img-remove"
                      @click.stop="removeImage(idx)"
                    >
                      <b-icon icon="x"></b-icon>
                    </b-button>
                  </div>

                  <!-- INFO -->
                  <div class="ord-product-panel ord-product-info">
                    <div class="ord-product-sku">{{ item.product_sku }}</div>
                    <div class="ord-product-name">{{ item.product_name || '—' }}</div>

                    <div class="ord-product-tags">
                      <span class="vs-chip" :class="getStatusTagClass(item.status)">
                        {{ getStatusText(item.status) }}
                      </span>
                      <span class="vs-chip">
                        <b-icon icon="box"></b-icon> {{ item.quantity || 0 }} units
                      </span>
                    </div>

                    <div class="ord-product-sub">
                      <div><b>Wholesale:</b> ${{ (item.unit_price || 0).toFixed(2) }}</div>
                      <div><b>Cubes:</b> {{ ((item.cubes || 0) * (item.quantity || 0)).toFixed(2) }}</div>
                    </div>

                    <!-- AVAILABILITY TILES (with lazy Load) -->
                    <div class="ord-avail">
                      <template v-if="!item.hasFetchedAvailability && !availabilityLoading[item.product_sku]">
                        <div class="ord-avail-item" style="grid-column: 1 / -1;">
                          <b-button size="sm" variant="outline-primary" block
                                    @click="fetchItemAvailability(item)"
                                    :disabled="saving">
                            <b-icon icon="arrow-repeat"></b-icon> Load availability
                          </b-button>
                        </div>
                      </template>
                      <template v-else-if="availabilityLoading[item.product_sku]">
                        <div class="ord-avail-item" style="grid-column: 1 / -1; text-align: center;">
                          <b-spinner small variant="primary"></b-spinner>
                          <small class="d-block">Loading…</small>
                        </div>
                      </template>
                      <template v-else>
                        <div class="ord-avail-item"><span>STK</span><b>{{ item.in_stock || 0 }}</b></div>
                        <div class="ord-avail-item is-warn"><span>RSV</span><b>{{ item.reserved || 0 }}</b></div>
                        <div class="ord-avail-item is-good"><span>AVL</span><b>{{ item.available || 0 }}</b></div>
                      </template>
                    </div>
                  </div>

                  <!-- QUANTITIES -->
                  <div class="ord-product-panel ord-product-qty">
                    <h6 class="ord-panel-title">Quantities</h6>
                    <div class="ord-qty-grid">
                      <label class="ord-qty-field">
                        <span>Qty</span>
                        <b-form-input size="sm" type="number" min="0"
                          v-model.number="item.quantity"
                          :disabled="saving || isItemLocked(item)"
                          @input="onQuantityInput(item)"></b-form-input>
                      </label>
                      <label class="ord-qty-field">
                        <span>Sch Qty</span>
                        <b-form-input size="sm" type="number" min="0" :max="item.quantity"
                          v-model.number="item.quantity_scheduled"
                          :disabled="saving || isItemLocked(item)"
                          @input="onScheduledQuantityInput(item)"
                          @blur="validateScheduledQuantity(item)"></b-form-input>
                      </label>
                      <label class="ord-qty-field">
                        <span>Shipped</span>
                        <b-form-input size="sm" type="number" min="0" :max="item.quantity"
                          v-model.number="item.quantity_shipped"
                          :disabled="saving || isItemLocked(item) || (item.status !== 2 && item.status !== 6)"
                          @input="onShippedQuantityInput(item)"
                          @blur="validateShippedQuantity(item)"></b-form-input>
                      </label>
                      <label class="ord-qty-field">
                        <span>Rcv Qty</span>
                        <b-form-input size="sm" type="number" min="0" :max="item.quantity_scheduled || item.quantity"
                          v-model.number="item.quantity_received"
                          :disabled="saving || isItemLocked(item) || (item.status !== 2 && item.status !== 6 && item.status !== 3 && item.status !== 4)"
                          @change="validateQuantity(item)"></b-form-input>
                      </label>
                      <label class="ord-qty-field">
                        <span>Item Status</span>
                        <b-form-select size="sm" :options="getStatusOptions(item)"
                          v-model.number="item.status"
                          :disabled="saving || isItemLocked(item)"
                          @change="handleStatusChange(item)"></b-form-select>
                      </label>
                      <label class="ord-qty-field">
                        <span>Del Status</span>
                        <b-form-select size="sm" :options="deliveryStatuses"
                          v-model.number="item.delivery_status"
                          :disabled="saving || isItemLocked(item)"
                          @change="markDirty"></b-form-select>
                      </label>
                    </div>
                  </div>

                  <!-- LOCATIONS & DATES -->
                  <div class="ord-product-panel ord-product-locs">
                    <h6 class="ord-panel-title">Locations &amp; Dates</h6>
                    <div class="ord-qty-grid">
                      <!-- ITEM LOC: per-row dynamic options -->
                      <label class="ord-qty-field">
                        <span>Item Loc (Source)</span>

                        <div v-if="reservedLocationRequests[item.product_sku]" class="text-center">
                          <b-spinner small variant="primary"></b-spinner>
                          <small class="ml-1">Loading…</small>
                        </div>

                        <b-form-select
                          v-else
                          size="sm"
                          :options="item.reservedLocationOptions && item.reservedLocationOptions.length
                            ? item.reservedLocationOptions
                            : (item.reserved_locsub
                                ? [{ value: item.reserved_locsub, text: getLocsubName(item.reserved_locsub) || item.reserved_locsub }]
                                : [])"
                          v-model="item.reserved_locsub"
                          :disabled="saving || isItemLocked(item)"
                          @change="markDirty"
                        ></b-form-select>
                      </label>

                      <label class="ord-qty-field">
                        <span>Receive Loc</span>
                        <b-form-select size="sm" :options="destLocOptions"
                          v-model="item.locsub"
                          :disabled="saving || isItemLocked(item)"
                          @change="markDirty"></b-form-select>
                      </label>

                      <label class="ord-qty-field">
                        <span>Schedule Date</span>
                        <date-picker v-model="item.schedule_date" format="MM-DD-YYYY"
                          valueType="format"
                          :disabled="saving || isItemLocked(item)"
                          @change="onScheduleDateChange(item, $event)"></date-picker>
                      </label>
                      <label class="ord-qty-field">
                        <span>Request ETA</span>
                        <date-picker v-model="item.requested_delivery_date" format="MM-DD-YYYY"
                          valueType="format"
                          :disabled="saving || isItemLocked(item)"
                          @change="onDeliveryDateChange(item, $event)"></date-picker>
                      </label>

                      <label class="ord-qty-field" style="grid-column: span 2;">
                        <span>Received Date</span>
                        <date-picker v-model="item.received_date" format="MM-DD-YYYY"
                          valueType="format"
                          :disabled-date="disableFutureDates"
                          :disabled="saving || isItemLocked(item) || item.status !== 3"
                          @change="onReceivedDateChange(item, $event)"></date-picker>
                      </label>
                    </div>
                  </div>

                  <!-- PRICE & COMMENTS -->
                  <div class="ord-product-panel ord-product-price">
                    <h6 class="ord-panel-title">Pricing &amp; Notes</h6>

                    <label class="ord-qty-field">
                      <span>Wholesale Price</span>
                      <b-form-input size="sm" type="number" step="0.01"
                        v-model.number="item.unit_price"
                        :disabled="saving || isItemLocked(item)"
                        @change="markDirty"></b-form-input>
                    </label>

                    <div class="ord-price-total">
                      <span>Line Total</span>
                      <strong>${{ ((item.quantity || 0) * (item.unit_price || 0)).toFixed(2) }}</strong>
                    </div>

                    <label class="ord-qty-field">
                      <span>Comments</span>
                      <b-form-input size="sm" v-model="item.comments"
                        :disabled="saving || isItemLocked(item)"
                        @change="markDirty"
                        placeholder="…"></b-form-input>
                    </label>

                    <div style="font-size: 11px; color: #A59D92; font-weight: 600; margin-top: auto;">
                      Line #{{ idx + 1 }}
                    </div>
                  </div>

                  <!-- ACTIONS -->
                  <div class="ord-product-panel ord-product-actions">
                    <b-button
                      size="sm"
                      variant="outline-danger"
                      @click="removeItem(idx)"
                      :disabled="saving || isItemLocked(item)"
                      class="w-100"
                    >
                      <b-icon icon="trash-fill"></b-icon>
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="w-100"
                      @click="$refs['fileInput-' + idx][0].click()"
                    >
                      <b-icon icon="camera"></b-icon>
                    </b-button>
                  </div>
                </article>
              </div>
            </div>
          </b-tab>

          <!-- ================= ORDER DETAILS ================= -->
          <b-tab title="Order Details" :title-link-class="'otab is-order'">
            <div style="padding: 12px;">
              <section class="ord-hero">
                <div class="ord-hero-cell">
                  <span>Order #</span>
                  <strong>#{{ header.id }}</strong>
                </div>
                <div class="ord-hero-cell">
                  <span>Status</span>
                  <span class="vs-chip" :class="getStatusTagClass(header.status)">
                    {{ getStatusText(header.status) }}
                  </span>
                </div>
                <div class="ord-hero-cell">
                  <span>Total Items</span>
                  <strong>{{ sumQty('quantity') }}</strong>
                </div>
                <div class="ord-hero-cell">
                  <span>Order Total</span>
                  <strong class="text-success">${{ totalCost.toFixed(2) }}</strong>
                </div>
              </section>

              <div class="detail-grid">
                <div class="detail-block">
                  <h4>
                    <span class="detail-icon is-dark"><b-icon icon="info-circle"></b-icon></span>
                    Order Information
                  </h4>
                  <dl class="detail-dl">
                    <dt>Order #</dt><dd>#{{ header.id }}</dd>
                    <dt>Order Type</dt><dd>{{ header.order_type || 'Standard' }}</dd>
                    <dt>Parent Order</dt><dd>{{ header.parent_order_id ? '#' + header.parent_order_id : '—' }}</dd>
                    <dt>Created By</dt><dd>{{ header.created_by || '—' }}</dd>
                    <dt>Created At</dt><dd>{{ formatDate(header.ts) }}</dd>
                    <dt>Approved</dt>
                    <dd>
                      <span class="vs-chip" :class="header.approved ? 'is-teal' : ''">
                        {{ header.approved ? 'YES' : 'NO' }}
                      </span>
                    </dd>
                  </dl>
                </div>

                <div class="detail-block">
                  <h4>
                    <span class="detail-icon is-teal"><b-icon icon="geo-alt-fill"></b-icon></span>
                    Locations
                  </h4>
                  <div class="loc-flow">
                    <div class="loc-endpoint">
                      <span class="loc-label">From</span>
                      <strong>{{ getLocName(header.selling_location) }}</strong>
                      <small>{{ getLocsubName(header.selling_location) }}</small>
                    </div>
                    <div class="loc-arrow">
                      <b-icon icon="arrow-right" font-scale="1.4"></b-icon>
                    </div>
                    <div class="loc-endpoint">
                      <span class="loc-label">To</span>
                      <strong>{{ getLocName(header.ordering_location) }}</strong>
                      <small>{{ getLocsubName(header.ordering_location) }}</small>
                    </div>
                  </div>
                </div>

                <div class="detail-block">
                  <h4>
                    <span class="detail-icon is-orange"><b-icon icon="calendar-event"></b-icon></span>
                    Dates &amp; Delivery
                  </h4>
                  <dl class="detail-dl">
                    <dt>Delivery Date</dt><dd>{{ header.delivery_date || '—' }}</dd>
                    <dt>Schedule Date</dt><dd>{{ header.schedule_date || '—' }}</dd>
                    <dt>Truck</dt><dd>{{ getTruckName(header.truck) || '—' }}</dd>
                  </dl>
                </div>

                <div class="detail-block">
                  <h4>
                    <span class="detail-icon is-teal"><b-icon icon="check2-circle"></b-icon></span>
                    Progress
                  </h4>
                  <div class="progress-stack">
                    <div class="progress-line">
                      <span>Ordered</span>
                      <div class="progress-bar-wrap">
                        <div class="progress-bar-fill is-ordered"
                          :style="{ width: progressPct('quantity') + '%' }"></div>
                      </div>
                      <b>{{ sumQty('quantity') }}</b>
                    </div>
                    <div class="progress-line">
                      <span>Scheduled</span>
                      <div class="progress-bar-wrap">
                        <div class="progress-bar-fill is-scheduled"
                          :style="{ width: progressPct('quantity_scheduled') + '%' }"></div>
                      </div>
                      <b>{{ sumQty('quantity_scheduled') }}</b>
                    </div>
                    <div class="progress-line">
                      <span>Shipped</span>
                      <div class="progress-bar-wrap">
                        <div class="progress-bar-fill is-shipped"
                          :style="{ width: progressPct('quantity_shipped') + '%' }"></div>
                      </div>
                      <b>{{ sumQty('quantity_shipped') }}</b>
                    </div>
                    <div class="progress-line">
                      <span>Received</span>
                      <div class="progress-bar-wrap">
                        <div class="progress-bar-fill is-received"
                          :style="{ width: progressPct('quantity_received') + '%' }"></div>
                      </div>
                      <b>{{ sumQty('quantity_received') }}</b>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-tab>

          <!-- ================= HISTORY ================= -->
          <b-tab title="History" :title-link-class="'otab is-history'">
            <div style="padding: 12px;">
              <div v-if="historyLoading" class="text-center py-4">
                <b-spinner small></b-spinner>
                <span class="ml-2">Loading history…</span>
              </div>
              <div v-else-if="historyError" class="text-center py-4 text-danger">
                <b-icon icon="exclamation-triangle-fill"></b-icon>
                {{ historyError }}
              </div>
              <div v-else-if="history.length === 0" class="text-center py-5 text-muted">
                <b-icon icon="clock-history" font-scale="3"></b-icon>
                <p class="mt-2">No history recorded for this order yet.</p>
              </div>
              <div v-else class="table-responsive">
                <table class="vs-mini-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>SKU</th>
                      <th>Description</th>
                      <th class="text-right">Qty Ordered</th>
                      <th class="text-right">Qty Received</th>
                      <th>Status</th>
                      <th>Drop Location</th>
                      <th>Changed By</th>
                      <th>Change Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(h, i) in history" :key="'h-' + i">
                      <td>{{ formatDate(h.change_date) }}</td>
                      <td><strong>{{ h.product_sku }}</strong></td>
                      <td>{{ h.item_description || '—' }}</td>
                      <td class="text-right">{{ h.quantity_ordered || 0 }}</td>
                      <td class="text-right">{{ h.quantity_received || 0 }}</td>
                      <td>
                        <span class="vs-chip" :class="getStatusTagClass(h.status)">
                          {{ getStatusText(h.status) }}
                        </span>
                      </td>
                      <td>{{ getLocsubName(h.drop_location) || h.drop_location || '—' }}</td>
                      <td>{{ h.changed_by || '—' }}</td>
                      <td>{{ h.change_type || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-tab>

          <!-- ================= PAYMENT DETAILS ================= -->
          <b-tab title="Payment Details" :title-link-class="'otab is-payment'">
            <div style="padding: 12px;">
              <div v-if="paymentsLoading" class="text-center py-4">
                <b-spinner small></b-spinner>
                <span class="ml-2">Loading payments…</span>
              </div>
              <div v-else-if="paymentsError" class="text-center py-4 text-danger">
                <b-icon icon="exclamation-triangle-fill"></b-icon>
                {{ paymentsError }}
              </div>
              <div v-else>
                <div class="payment-summary">
                  <div>
                    <span>Total Order Value</span>
                    <strong>${{ totalCost.toFixed(2) }}</strong>
                  </div>
                  <div>
                    <span>Total Paid</span>
                    <strong class="text-success">${{ totalPaid.toFixed(2) }}</strong>
                  </div>
                  <div>
                    <span>Balance Due</span>
                    <strong :class="balanceDue > 0 ? 'text-danger' : 'text-success'">
                      ${{ balanceDue.toFixed(2) }}
                    </strong>
                  </div>
                </div>

                <h4 style="font-size: 14px; font-weight: 700; margin: 16px 0 8px;">
                  Payment Transactions
                </h4>

                <div v-if="payments.length === 0" class="text-muted text-center py-4">
                  <b-icon icon="credit-card" font-scale="2"></b-icon>
                  <p class="mt-2 mb-0">No payment transactions recorded.</p>
                </div>
                <div v-else class="table-responsive">
                  <table class="vs-mini-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Reference</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, i) in payments" :key="'p-' + i">
                        <td>{{ formatDate(p.ts) }}</td>
                        <td>{{ (p.value || '—').toUpperCase() }}</td>
                        <td>
                          <template v-if="p.name && p.name !== 'N/A'">{{ p.name }}</template>
                          <template v-else-if="p.lease_id">Lease {{ p.lease_id }}</template>
                          <template v-else>—</template>
                        </td>
                        <td class="text-right"><b>${{ formatCurrency(p.amount) }}</b></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style="margin-top: 14px;">
                  <b-button class="vs-btn-primary" @click="openPaymentForm" :disabled="paymentsLoading">
                    <b-icon icon="plus-circle"></b-icon> Add Payment
                  </b-button>
                </div>

                <div v-if="showNewPayment" class="mt-3" style="background: #F7F5F2; padding: 14px; border-radius: 12px;">
                  <div class="row">
                    <div class="col-md-3">
                      <label class="small text-muted mb-1">Amount</label>
                      <b-input v-model.number="newPayment.amount" type="number" step="0.01" min="0"></b-input>
                    </div>
                    <div class="col-md-3">
                      <label class="small text-muted mb-1">Method</label>
                      <b-form-select v-model="newPayment.method" :options="paymentMethods"></b-form-select>
                    </div>
                    <div class="col-md-4">
                      <label class="small text-muted mb-1">Reference (last 4 digits / lease id)</label>
                      <b-input v-model="newPayment.reference"></b-input>
                    </div>
                    <div class="col-md-2 d-flex align-items-end" style="gap: 6px;">
                      <b-button class="vs-btn-primary" :disabled="savingPayment" @click="submitNewPayment">
                        <b-spinner small v-if="savingPayment"></b-spinner>
                        <b-icon v-else icon="check2"></b-icon>
                      </b-button>
                      <b-button class="vs-btn-ghost" @click="cancelNewPayment">Cancel</b-button>
                    </div>
                  </div>
                  <b-alert v-if="newPayment.error" show variant="danger" class="mb-0 mt-2">
                    {{ newPayment.error }}
                  </b-alert>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>

    <template #modal-footer>
      <div style="display: flex; justify-content: space-between; width: 100%;">
        <span style="color: #736C63; font-size: 13px;">
          <template v-if="hasChanges">Unsaved changes</template>
          <template v-else>No changes</template>
        </span>
        <div style="display: flex; gap: 8px;">
          <b-button class="vs-btn-ghost" @click="$bvModal.hide('modal-view-store-order' + orderId)">
            Close
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: "StoreOrderDetailsModal",
  components: { DatePicker },
  props: { orderId: { type: [Number, String], required: true } },
  data() {
    return {
      loading: false,
      fetchError: null,
      header: null,
      items: [],
      originalItems: [],
      activeTab: 0,
      hasChanges: false,
      saving: false,
      approving: false,

      bulkStatus: '',
      bulkStatusOptions: [
        { value: 1, text: 'ORDERED' },
        { value: 2, text: 'SCHEDULED' },
        { value: 6, text: 'SHIPPED' },
        { value: 3, text: 'RECEIVED' },
        { value: 4, text: 'PARTIAL' },
        { value: 5, text: 'COMPLETED' },
      ],

      historyLoading: false,
      historyError: null,
      history: [],

      paymentsLoading: false,
      paymentsError: null,
      payments: [],
      showNewPayment: false,
      savingPayment: false,
      newPayment: { amount: null, method: 'cash', reference: '', error: null },

      packet: {},

      deliveryStatuses: [
        { value: 0, text: 'PENDING' },
        { value: 1, text: 'SCHEDULED' },
        { value: 2, text: 'IN TRANSIT' },
        { value: 3, text: 'DELIVERED' },
      ],

      paymentMethods: [
        { value: 'cash', text: 'Cash' },
        { value: 'check', text: 'Check' },
        { value: 'cc', text: 'Credit Card' },
        { value: 'store_credit', text: 'Store Credit' },
        { value: 'finance', text: 'Finance' },
      ],

      // ✅ Availability loading state
      availabilityLoading: {},
      reservedLocationRequests: {},
      isLoadingAllAvailability: false,
    };
  },
  computed: {
    totalCost() {
      return this.items.reduce((s, i) => s + (parseFloat(i.quantity || 0) * parseFloat(i.unit_price || 0)), 0);
    },
    totalPaid() {
      return this.payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    },
    balanceDue() {
      return Math.max(0, this.totalCost - this.totalPaid);
    },
    destLocOptions() {
      const locsubs = Array.isArray(this.packet.locsubs) ? this.packet.locsubs : [];
      if (!this.header) return [];
      return locsubs
        .filter(l => {
          const starts = String(l.value).startsWith(String(this.header.ordering_location) + '|');
          const isDamage = (l.text || '').toLowerCase().includes('damage');
          return starts && !isDamage;
        })
        .map(l => ({ value: l.value, text: l.text }));
    },
    isAdmin() {
      return this.packet.userDetails && this.packet.userDetails.userrole === 'admin';
    },
    isAdminOrManager() {
      const r = this.packet.userDetails && this.packet.userDetails.userrole;
      return ['admin', 'officemanager'].includes(r);
    },
    canApprove() {
      if (!this.header) return false;
      const headerHas = !!this.header.delivery_date;
      const anyItem = this.items.some(i => !!i.requested_delivery_date);
      return headerHas || anyItem;
    },
    canSave() {
      return this.hasChanges;
    },
  },
  mounted() {
    try {
      this.packet = JSON.parse(localStorage.packet || '{}');
      const ud = JSON.parse(localStorage.userDetails || '{}');
      this.packet.userDetails = ud;
    } catch (e) { this.packet = {}; }
    this.$bvModal.show('modal-view-store-order' + this.orderId);
    this.loadAll();
  },
  methods: {
    /* ============================================================
     *  LOAD
     * ============================================================ */
    async loadAll() {
      this.loading = true;
      this.fetchError = null;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getSyncP(
            "/api/sales/get_transfer_order_items/",
            { id: this.orderId },
            this, resolve, reject
          );
        });
        if (!res || !res.data || res.data.error) {
          this.fetchError = (res && res.data && res.data.error) || "Failed to load order.";
          return;
        }
        const payload = res.data;
        const items = Array.isArray(payload.result) ? payload.result : [];
        this.header = {
          id: this.orderId,
          ordering_location: payload.ordering_location || null,
          selling_location: payload.selling_location || null,
          delivery_date: payload.main_delivery_date || null,
          schedule_date: payload.main_schedule_date || null,
          status: items.length ? parseInt(items[0].status || 1) : 1,
          created_by: items.length ? (items[0].created_by || null) : null,
          ts: items.length ? (items[0].ts || null) : null,
          order_type: null,
          parent_order_id: null,
          truck: null,
          approved: false,
        };

        // Enrich from order list endpoint
        try {
          const headerRes = await new Promise((resolve, reject) => {
            Requestor.getSyncP("/api/sales/get_transfer_orders/", {}, this, resolve, reject);
          });
          const allOrders = (headerRes && headerRes.data && Array.isArray(headerRes.data.result))
            ? headerRes.data.result
            : [];
          const match = allOrders.find(o => String(o.id) === String(this.orderId));
          if (match) {
            this.header = {
              ...this.header,
              created_by:        match.created_by      || this.header.created_by,
              ts:                match.ts              || this.header.ts,
              status:            match.status          != null ? match.status : this.header.status,
              truck:             match.truck           || this.header.truck,
              order_type:        match.order_type      || null,
              parent_order_id:   match.parent_order_id || null,
              delivery_date:     match.delivery_date   || this.header.delivery_date,
              schedule_date:     match.schedule_date   || this.header.schedule_date,
              ordering_location: match.ordering_location || this.header.ordering_location,
              selling_location:  match.selling_location  || this.header.selling_location,
              approved:          !!match.approved,
            };
          }
        } catch (hdrErr) {
          console.warn("Could not enrich header:", hdrErr);
        }

        this.items = items.map(i => {
          const reservedOption = i.reserved_locsub
            ? [{ value: i.reserved_locsub, text: this.getLocsubName(i.reserved_locsub) || i.reserved_locsub }]
            : [];

          return {
            ...i,
            unit_price:         parseFloat(i.wholesale_price || i.unit_price || 0),
            quantity:           parseInt(i.quantity || 0),
            quantity_scheduled: parseInt(i.quantity_scheduled || 0),
            quantity_shipped:   parseInt(i.quantity_shipped || 0),
            quantity_received:  parseInt(i.quantity_received || 0),
            status:             parseInt(i.status || 1),
            original_status:    parseInt(i.status || 1),
            delivery_status:    parseInt(i.delivery_status || 0),
            cubes:              parseFloat(i.cubes || 0),
            requested_delivery_date: i.delivery_date || i.requested_delivery_date || null,
            images: i.pictures ? (typeof i.pictures === 'string' ? JSON.parse(i.pictures) : i.pictures) : [],

            // ✅ Availability + Item-Loc state
            in_stock: 0,
            reserved: 0,
            available: 0,
            hasFetchedAvailability: false,
            reservedLocationOptions: reservedOption,
          };
        });
        this.originalItems = JSON.parse(JSON.stringify(this.items));
        this.hasChanges = false;
        this.loadHistory();
        this.loadPayments();
      } catch (e) {
        console.error(e);
        this.fetchError = "Network error loading order.";
      } finally {
        this.loading = false;
      }
    },

    async loadHistory() {
      this.historyLoading = true;
      this.historyError = null;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getSyncP("/api/sales/get_transfer_order_history/", { id: this.orderId }, this, resolve, reject);
        });
        if (res.data && !res.data.error) {
          this.history = Array.isArray(res.data.result) ? res.data.result : [];
        } else {
          this.historyError = (res.data && res.data.error) || "Could not load history.";
        }
      } catch (e) {
        this.historyError = "Network error loading history.";
      } finally {
        this.historyLoading = false;
      }
    },

    async loadPayments() {
      this.paymentsLoading = true;
      this.paymentsError = null;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getSyncP("/api/sales/get_transfer_order_payments/", { id: this.orderId }, this, resolve, reject);
        });
        if (res.data && !res.data.error) {
          this.payments = Array.isArray(res.data.result) ? res.data.result : [];
        } else {
          this.paymentsError = (res.data && res.data.error) || "Could not load payments.";
        }
      } catch (e) {
        this.paymentsError = "Network error loading payments.";
      } finally {
        this.paymentsLoading = false;
      }
    },

    /* ============================================================
     *  AVAILABILITY (lazy per-item, like TransferOrderModal)
     * ============================================================ */
    async fetchItemAvailability(item) {
      if (!item || !item.product_sku) return;
      if (this.availabilityLoading[item.product_sku]) return;

      try {
        this.$set(this.availabilityLoading, item.product_sku, true);

        const response = await new Promise((resolve, reject) => {
          Requestor.getSyncP(
            "/api/product/get_product_list_v4_transfer_order/",
            {
              sku: item.product_sku,
              selling_location: this.header.selling_location
            },
            this, resolve, reject
          );
        });

        if (!response || !response.data) return;
        if (response.data.error) {
          console.error("Availability error:", response.data.error);
          return;
        }

        const p = (response.data.result && response.data.result[0]) || {};
        this.$set(item, 'in_stock',  p.in_stock  || 0);
        this.$set(item, 'reserved',  p.reserved  || 0);
        this.$set(item, 'available', p.available || 0);
        this.$set(item, 'hasFetchedAvailability', true);

        if (item.quantity > 0) {
          this.$set(this.reservedLocationRequests, item.product_sku, true);
          const opts = await this.fetchReservedLocations(item);
          this.$set(item, 'reservedLocationOptions', opts);
          if (opts.length && !item.reserved_locsub) {
            this.$set(item, 'reserved_locsub', opts[0].value);
          }
          this.$delete(this.reservedLocationRequests, item.product_sku);
        }
      } catch (e) {
        console.error("Availability fetch failed:", e);
      } finally {
        this.$delete(this.availabilityLoading, item.product_sku);
      }
    },

    async fetchReservedLocations(item) {
      const presetOption = item.reserved_locsub
        ? [{ value: item.reserved_locsub, text: this.getLocsubName(item.reserved_locsub) || item.reserved_locsub }]
        : [];

      try {
        const response = await new Promise((resolve, reject) => {
          Requestor.getSyncP(
            "/api/product/get_available_inventory_dropdown/",
            {
              sku: item.product_sku,
              selling_location: this.header.selling_location
            },
            this, resolve, reject
          );
        });

        let data = null;
        if (response && typeof response === 'object') {
          if ('error' in response || 'result' in response) data = response;
          else if (response.data && ('error' in response.data || 'result' in response.data)) data = response.data;
        }

        if (!data || data.error) return presetOption;

        const list = Array.isArray(data.result) ? data.result : [];
        const merged = [...presetOption];
        list.forEach(o => {
          if (!merged.some(m => String(m.value) === String(o.value))) {
            merged.push({ value: o.value, text: o.text });
          }
        });
        return merged.length ? merged : presetOption;
      } catch (e) {
        console.error("Reserved locations fetch failed:", e);
        return presetOption;
      }
    },

    async loadAllAvailability() {
  if (!this.items.length) return;
  this.isLoadingAllAvailability = true;
  let ok = 0, err = 0;
  for (const it of this.items) {
    if (it.hasFetchedAvailability) { ok++; continue; }
    try {
      await this.fetchItemAvailability(it);
      ok++;
    } catch (e) {           // ✅ explicit binding — fixes the syntax error
      err++;
    }
  }
  this.isLoadingAllAvailability = false;
  this.$bvToast.toast(
    `Availability loaded: ${ok} ok, ${err} failed`,
    { variant: err ? 'warning' : 'success', autoHideDelay: 2500 }
  );
},
    /* ============================================================
     *  APPROVE
     * ============================================================ */
    async approveOrder() {
      if (this.approving) return;
      if (!this.canApprove) {
        this.$bvToast.toast("Set a Request ETA first before approving.", { variant: 'warning', autoHideDelay: 2500 });
        return;
      }
      this.approving = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postSyncP("/api/sales/approve_transfer_order/", { id: this.orderId, approved: true }, this, resolve, reject);
        });
        if (res.data && res.data.error) {
          this.$bvToast.toast(res.data.error, { variant: 'danger' });
        } else {
          this.header.approved = true;
          this.$bvToast.toast("Order approved.", { variant: 'success', autoHideDelay: 2000 });
        }
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Error approving order.", { variant: 'danger' });
      } finally {
        this.approving = false;
      }
    },

    /* ============================================================
     *  DIRTY / RESET
     * ============================================================ */
    markDirty() { this.hasChanges = true; },
    resetItems() {
      this.items = JSON.parse(JSON.stringify(this.originalItems));
      this.hasChanges = false;
    },
    removeItem(idx) {
      this.items.splice(idx, 1);
      this.markDirty();
    },

    /* ============================================================
     *  IMAGE
     * ============================================================ */
    handleImageUpload(event, idx) {
      const file = event.target.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.$bvToast.toast('Invalid image.', { variant: 'warning' });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.items[idx].images = [{ raw: e.target.result }];
        this.markDirty();
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    },
    removeImage(idx) {
      this.items[idx].images = [];
      this.markDirty();
    },

    /* ============================================================
     *  STATUS + QUANTITY LOGIC (mirrors TransferOrderModal)
     * ============================================================ */
    isItemLocked(item) {
      if (item.original_status === 3 && item.status === 3) return true;
      return false;
    },

    disableFutureDates(date) {
      return date > new Date();
    },

    getStatusOptions(item) {
      const scheduledQty = parseInt(item.quantity_scheduled) || 0;
      const receivedQty  = parseInt(item.quantity_received)  || 0;
      const isScheduleComplete = scheduledQty > 0;

      return [
        { value: 1, text: "ORDERED",   disabled: false },
        { value: 2, text: "SCHEDULED", disabled: scheduledQty > 0 },
        { value: 6, text: "SHIPPED",   disabled: !isScheduleComplete },
        { value: 3, text: "RECEIVED",  disabled: !isScheduleComplete },
        {
          value: 4, text: "PARTIAL",
          disabled: !isScheduleComplete || receivedQty <= 0 || receivedQty === scheduledQty
        },
        { value: 5, text: "COMPLETED", disabled: false },
      ];
    },

    handleStatusChange(item) {
      const scheduledQty = parseInt(item.quantity_scheduled) || 0;
      const receivedQty  = parseInt(item.quantity_received)  || 0;
      const originalStatus = item.original_status || item.status;

      if (item.status === 6 && scheduledQty <= 0) {
        this.$bvToast.toast("Cannot mark as SHIPPED: schedule it first.", { variant: 'warning', autoHideDelay: 2500 });
        this.$nextTick(() => { item.status = originalStatus; });
        return;
      }
      if (item.status === 3 && scheduledQty <= 0) {
        this.$bvToast.toast("Cannot mark as RECEIVED: schedule it first.", { variant: 'warning', autoHideDelay: 2500 });
        this.$nextTick(() => { item.status = originalStatus; });
        return;
      }
      if (item.status === 4) {
        if (receivedQty <= 0 || receivedQty >= scheduledQty) {
          this.$bvToast.toast("PARTIAL requires received qty > 0 and < scheduled.", { variant: 'warning', autoHideDelay: 2500 });
          this.$nextTick(() => { item.status = originalStatus; });
          return;
        }
        if (!item.received_date) this.$set(item, 'received_date', moment().format('MM-DD-YYYY'));
      }
      if (item.status === 2) {
        if (!item.schedule_date) this.$set(item, 'schedule_date', moment().format('MM-DD-YYYY'));
        if (!item.quantity_scheduled || item.quantity_scheduled <= 0) {
          this.$set(item, 'quantity_scheduled', item.quantity);
        }
      }
      if (item.status === 3) {
        this.$set(item, 'received_date', moment().format('MM-DD-YYYY'));
        if (!item.quantity_received || item.quantity_received === 0) {
          this.$set(item, 'quantity_received', scheduledQty);
        }
      }
      if (originalStatus === 3 && item.status !== 3 && item.status !== 4) {
        this.$set(item, 'received_date', null);
        this.$set(item, 'quantity_received', 0);
      }
      this.markDirty();
    },

    onQuantityInput(item) {
      const qty = parseInt(item.quantity, 10);
      if (isNaN(qty) || qty <= 0) {
        this.$set(item, 'quantity', 0);
        this.$set(item, 'quantity_shipped', 0);
        this.markDirty();
        return;
      }
      this.$set(item, 'quantity', qty);
      if (item.quantity_scheduled > qty) item.quantity_scheduled = qty;
      if (item.quantity_shipped > qty) item.quantity_shipped = qty;
      this.markDirty();
    },

    onScheduledQuantityInput(item) {
      clearTimeout(this._schedTimer);
      this._schedTimer = setTimeout(() => this.validateScheduledQuantity(item), 300);
    },
    validateScheduledQuantity(item) {
      this.$nextTick(() => {
        if (item.quantity_scheduled < 0) item.quantity_scheduled = 0;
        if (item.quantity_scheduled > item.quantity) item.quantity_scheduled = item.quantity;

        if (item.quantity_scheduled > 0 && item.status !== 3) {
          if (item.status !== 2) {
            item.status = 2;
            if (!item.schedule_date) this.$set(item, 'schedule_date', moment().format('MM-DD-YYYY'));
          }
        } else if (item.quantity_scheduled === 0 && item.status === 2) {
          item.status = 1;
        }
        this.markDirty();
      });
    },

    onShippedQuantityInput() {
      this.markDirty();
    },
    validateShippedQuantity(item) {
      this.$nextTick(() => {
        let v = Number(item.quantity_shipped) || 0;
        if (v < 0) v = 0;
        if (v > item.quantity) v = item.quantity;
        item.quantity_shipped = v;
        this.markDirty();
      });
    },

    validateQuantity(item) {
      if (item.status === 3) {
        const scheduledQty = parseInt(item.quantity_scheduled) || 0;
        const receivedQty  = parseInt(item.quantity_received)  || 0;
        if (receivedQty > scheduledQty) item.quantity_received = scheduledQty;
        return;
      }

      const scheduledQty = parseInt(item.quantity_scheduled) || 0;
      const max = scheduledQty > 0 ? scheduledQty : item.quantity;
      this.$nextTick(() => {
        let received = Number(item.quantity_received) || 0;
        if (received < 0) received = 0;
        if (received > max) received = max;
        item.quantity_received = received;

        if (received > 0 && scheduledQty > 0) {
          if (received === scheduledQty) {
            const orderedQty = parseInt(item.quantity) || 0;
            if (scheduledQty === orderedQty) {
              item.status = 3;
              if (!item.received_date) item.received_date = moment().format('MM-DD-YYYY');
            } else {
              item.status = 3;
            }
          } else {
            item.status = 4;
            if (!item.received_date) item.received_date = moment().format('MM-DD-YYYY');
          }
        } else {
          item.status = item.original_status || 1;
          item.received_date = null;
        }
        this.markDirty();
      });
    },

    onScheduleDateChange(item, date) {
      if (date && item.status !== 3) {
        this.$nextTick(() => {
          if (!item.quantity_scheduled || item.quantity_scheduled <= 0) {
            this.$set(item, 'quantity_scheduled', item.quantity);
          }
          item.status = 2;
        });
      }
      if (!date && item.status === 2 && (!item.quantity_scheduled || item.quantity_scheduled <= 0)) {
        this.$nextTick(() => { item.status = 1; });
      }
      this.markDirty();
    },

    onDeliveryDateChange(item, date) {
      if (date && item.status !== 3) {
        this.$nextTick(() => { item.status = 2; });
      }
      if (!date && item.status === 2) {
        this.$nextTick(() => { item.status = 1; });
      }
      this.markDirty();
    },

    onReceivedDateChange(item, date) {
      const today = moment().format('MM-DD-YYYY');
      if (date !== today) {
        this.$bvToast.toast("Received date must be today's date.", { variant: 'warning', autoHideDelay: 2000 });
        this.$set(item, 'received_date', today);
        return;
      }
      if (date) {
        const scheduledQty = parseInt(item.quantity_scheduled) || 0;
        const receivedQty  = parseInt(item.quantity_received)  || 0;
        if (scheduledQty <= 0) {
          this.$bvToast.toast("Schedule first.", { variant: 'warning' });
          this.$set(item, 'received_date', null);
          return;
        }
        if (receivedQty > 0 && receivedQty <= scheduledQty) {
          item.status = receivedQty === scheduledQty ? 3 : 4;
        } else {
          this.$set(item, 'received_date', null);
        }
      }
      this.markDirty();
    },

    /* ============================================================
     *  BULK STATUS
     * ============================================================ */
    applyBulkStatus(statusValue) {
      if (!statusValue) return;
      let updated = 0, schedFilled = 0;
      this.items = this.items.map(item => {
        if (item.original_status === 3) return item;
        item.status = statusValue;
        updated++;

        if (statusValue === 2) {
          const orderedQty = parseInt(item.quantity) || 0;
          if (orderedQty > 0) {
            item.quantity_scheduled = orderedQty;
            schedFilled++;
            if (!item.schedule_date) item.schedule_date = moment().format('MM-DD-YYYY');
          }
        }
        if (statusValue === 6) {
          const orderedQty = parseInt(item.quantity) || 0;
          if (orderedQty > 0 && (!item.quantity_scheduled || item.quantity_scheduled === 0)) {
            item.quantity_scheduled = orderedQty;
            schedFilled++;
          }
        }
        if (statusValue === 3) {
          const orderedQty = parseInt(item.quantity) || 0;
          if (orderedQty > 0) {
            if (!item.quantity_scheduled || item.quantity_scheduled === 0) {
              item.quantity_scheduled = orderedQty;
              schedFilled++;
            }
            item.quantity_received = item.quantity_scheduled || orderedQty;
            item.received_date = moment().format('MM-DD-YYYY');
          }
        }
        return item;
      });

      this.$bvToast.toast(
        `Applied ${this.getStatusText(statusValue)} to ${updated} items` +
        (schedFilled ? `, filled scheduled qty for ${schedFilled}.` : '.'),
        { variant: 'success', autoHideDelay: 2500 }
      );
      this.bulkStatus = '';
      this.markDirty();
    },

    /* ============================================================
     *  SAVE
     * ============================================================ */
    async saveItems() {
      const errors = [];
      for (const item of this.items) {
        if (item.status === 6 && (!item.quantity_scheduled || item.quantity_scheduled <= 0)) {
          errors.push(`Item ${item.product_sku}: cannot be SHIPPED without schedule.`);
        }
        if (item.status === 3 && (!item.quantity_scheduled || item.quantity_scheduled <= 0)) {
          errors.push(`Item ${item.product_sku}: cannot be RECEIVED without schedule.`);
        }
      }
      if (errors.length) {
        this.$bvToast.toast(errors.join(' '), { variant: 'warning', autoHideDelay: 5000 });
        return;
      }

      this.saving = true;
      try {
        const payloadItems = this.items.map(item => ({
          product_sku:        item.product_sku,
          product_name:       item.product_name,
          product_id:         item.product_id,
          quantity:           parseInt(item.quantity) || 0,
          quantity_scheduled: parseInt(item.quantity_scheduled) || 0,
          quantity_shipped:   parseInt(item.quantity_shipped) || 0,
          quantity_received:  parseInt(item.quantity_received) || 0,
          wholesale_price:    parseFloat(item.unit_price) || 0,
          status:             parseInt(item.status) || 1,
          delivery_status:    parseInt(item.delivery_status) || 0,
          delivery_date:      item.requested_delivery_date || null,
          schedule_date:      item.schedule_date || null,
          received_date:      item.received_date || null,
          locsub:             item.locsub,
          reserved_locsub:    item.reserved_locsub,
          shipping_location:  item.shipping_location || '47',
          comments:           item.comments || null,
          images:             item.images || [],
        }));

        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP("/api/sales/save_transfer_order_items/", {
            id: this.orderId,
            username: this.packet.userDetails ? this.packet.userDetails.username : 'system',
            items: payloadItems,
          }, this, resolve, reject);
        });
        if (res.data && res.data.error) {
          this.$bvToast.toast(res.data.error, { variant: 'danger', autoHideDelay: 3000 });
          return;
        }
        this.$bvToast.toast('Order saved successfully!', { variant: 'success', autoHideDelay: 2000 });
        await this.loadAll();
      } catch (e) {
        console.error(e);
        this.$bvToast.toast('Error saving order.', { variant: 'danger' });
      } finally {
        this.saving = false;
      }
    },

    /* ============================================================
     *  PAYMENTS
     * ============================================================ */
    openPaymentForm() {
      this.showNewPayment = true;
      this.newPayment = { amount: null, method: 'cash', reference: '', error: null };
    },
    cancelNewPayment() {
      this.showNewPayment = false;
      this.newPayment = { amount: null, method: 'cash', reference: '', error: null };
    },
    async submitNewPayment() {
      if (!this.newPayment.amount || this.newPayment.amount <= 0) {
        this.newPayment.error = 'Please enter a valid amount.';
        return;
      }
      this.savingPayment = true;
      this.newPayment.error = null;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP("/api/sales/add_transfer_order_payment/", {
            transfer_order_id: this.orderId,
            amount:            parseFloat(this.newPayment.amount),
            payment_method:    this.newPayment.method,
            reference:         this.newPayment.reference || null,
            username:          this.packet.userDetails ? this.packet.userDetails.username : 'system',
          }, this, resolve, reject);
        });
        if (res.data && res.data.error) {
          this.newPayment.error = res.data.error;
          return;
        }
        this.$bvToast.toast('Payment added.', { variant: 'success', autoHideDelay: 2000 });
        this.showNewPayment = false;
        this.loadPayments();
      } catch (e) {
        console.error(e);
        this.newPayment.error = 'Network error saving payment.';
      } finally {
        this.savingPayment = false;
      }
    },

    /* ============================================================
     *  HELPERS
     * ============================================================ */
    sumQty(field) {
      return this.items.reduce((s, i) => s + (parseInt(i[field]) || 0), 0);
    },
    progressPct(field) {
      const total = this.sumQty('quantity');
      if (total <= 0) return 0;
      return Math.min(100, Math.round((this.sumQty(field) / total) * 100));
    },
    getLocName(value) {
      if (!value) return '—';
      if (String(value) === '0') return 'MECCA WHOLESALE';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === String(value));
      return found ? found.text : value;
    },
    getLocsubName(value) {
      if (!value) return '';
      const locsubs = this.packet.locsubs || [];
      const found = locsubs.find(l => String(l.value) === String(value));
      return found ? found.text : '';
    },
    getTruckName(id) {
      if (!id) return '';
      const trucks = (this.packet.housetrucks || []);
      const found = trucks.find(t => String(t.value) === String(id));
      return found ? found.text : id;
    },
    getStatusText(status) {
      const map = {1:'ORDERED', 2:'SCHEDULED', 3:'RECEIVED', 4:'PARTIAL', 5:'COMPLETED', 6:'SHIPPED'};
      return map[status] || 'UNKNOWN';
    },
    getStatusTagClass(status) {
      const map = {1:'is-warn', 2:'is-teal', 3:'is-teal', 4:'is-warn', 5:'is-teal', 6:'is-warn'};
      return map[status] || '';
    },
    formatDate(d) {
      return d ? moment(d).format("MM-DD-YYYY HH:mm") : "—";
    },
    formatCurrency(v) {
      return (parseFloat(v) || 0).toFixed(2);
    },
  }
}
</script>

<style scoped>
.order-view { font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #221F1C; font-size: 14px; }
.order-view-tabs >>> .nav-tabs { border-bottom: 3px solid #E5E1DA; gap: 4px; }
.order-view-tabs >>> .nav-tabs .nav-link { font-weight: 700; font-size: 14px; color: #736C63; padding: 10px 18px; border-radius: 10px 10px 0 0; border: 1.5px solid #E5E1DA; border-bottom: none; background: #F7F5F2; }
.order-view-tabs >>> .nav-tabs .nav-link.active { color: #fff; border-color: transparent; }
.order-view-tabs >>> .nav-tabs .nav-link.active.is-product { background: #221F1C; }
.order-view-tabs >>> .nav-tabs .nav-link.active.is-order   { background: #0F6E6B; }
.order-view-tabs >>> .nav-tabs .nav-link.active.is-history { background: #7A520A; }
.order-view-tabs >>> .nav-tabs .nav-link.active.is-payment { background: #8E2A21; }
.order-view-tabs >>> .tab-content { border: 1.5px solid #E5E1DA; border-top: none; border-radius: 0 0 12px 12px; background: #fff; }
.vs-card { background: #fff; border: 1px solid #E5E1DA; border-radius: 18px; padding: 16px 20px; }
.vs-icon { width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0; background: #EE7A22; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 18px; }
.vs-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; background: #F1EEE9; color: #4A4540; }
.vs-chip.is-warn { background: #FFF4DC; color: #8A5E09; }
.vs-chip.is-teal { background: #E2F2F0; color: #0F6E6B; }
.vs-chip.is-orange { background: #FFF1E4; color: #D4620F; }
.vs-chip.is-danger { background: #FBE9E7; color: #C93C30; }
.vs-count { font-size: 13px; font-weight: 700; color: #736C63; }
.vs-btn-primary.btn, .vs-btn-ghost.btn { border-radius: 10px !important; font-weight: 700; font-size: 13px; padding: 6px 14px; display: inline-flex; align-items: center; gap: 6px; box-shadow: none !important; }
.vs-btn-primary.btn { background: #EE7A22 !important; border: 1.5px solid #EE7A22 !important; color: #fff !important; }
.vs-btn-primary.btn:hover:not(:disabled) { background: #D4620F !important; border-color: #D4620F !important; }
.vs-btn-ghost.btn { background: #fff !important; color: #221F1C !important; border: 1.5px solid #E5E1DA !important; }
.vs-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }
.vs-mini-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.vs-mini-table thead th { padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 800; border-bottom: 2px solid #E5E1DA; white-space: nowrap; color: #736C63; background: #F7F5F2; }
.vs-mini-table tbody td { padding: 8px 12px; border-bottom: 1px solid #F1EEE9; vertical-align: middle; }
.vs-mini-table tbody tr:hover { background: #FAF9F7; }
.ord-product-list { display: flex; flex-direction: column; gap: 14px; }
.ord-product-card { display: grid; grid-template-columns: 200px 1fr 1.2fr 1.5fr 0.8fr 60px; gap: 8px; background: #fff; border: 1px solid #E5E1DA; border-left: 5px solid #E5E1DA; border-radius: 16px; padding: 10px; align-items: stretch; }
.ord-product-card.is-status-1 { border-left-color: #C98A12; }
.ord-product-card.is-status-2 { border-left-color: #2B83B5; }
.ord-product-card.is-status-3 { border-left-color: #0F6E6B; }
.ord-product-card.is-status-4 { border-left-color: #D4620F; }
.ord-product-card.is-status-5 { border-left-color: #221F1C; }
.ord-product-card.is-status-6 { border-left-color: #8A5E09; }
.ord-product-img { position: relative; border-radius: 12px; background: linear-gradient(160deg, #F7F3EE, #EFEAE3); display: flex; align-items: center; justify-content: center; overflow: hidden; min-height: 100px; }
.ord-product-img img { width: 100%; height: 100%; max-height: 120px; object-fit: contain; }
.ord-product-img-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #A59D92; cursor: pointer; padding: 10px; }
.ord-product-img-empty span { font-size: 10px; font-weight: 700; text-transform: uppercase; }
.ord-product-img-remove { position: absolute; top: 4px; right: 4px; }
.ord-product-panel { background: #FAF9F7; border-radius: 12px; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.ord-panel-title { font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: #736C63; margin: 0; }
.ord-product-info { background: #F7F5F2; }
.ord-product-sku { font-family: 'Menlo', monospace; font-size: 11.5px; font-weight: 700; color: #D4620F; }
.ord-product-name { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 700; color: #221F1C; line-height: 1.3; word-break: break-word; }
.ord-product-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.ord-product-sub { font-size: 11.5px; color: #736C63; display: flex; flex-direction: column; gap: 2px; }
.ord-product-sub b { color: #221F1C; }
.ord-avail { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; margin-top: auto; }
.ord-avail-item { background: #F1EEE9; border-radius: 8px; padding: 6px 4px; text-align: center; }
.ord-avail-item span { display: block; font-size: 9.5px; font-weight: 800; color: #736C63; text-transform: uppercase; }
.ord-avail-item b { font-family: 'Sora', sans-serif; font-size: 15px; color: #221F1C; }
.ord-avail-item.is-warn { background: #FFF4DC; }
.ord-avail-item.is-warn b { color: #8A5E09; }
.ord-avail-item.is-good { background: #E2F2F0; }
.ord-avail-item.is-good b { color: #0F6E6B; }
.ord-qty-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 12px; align-content: start; }
.ord-qty-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.ord-qty-field > span { font-size: 10px; font-weight: 800; color: #736C63; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ord-qty-field >>> .form-control { text-overflow: ellipsis; }
.ord-qty-field >>> .mx-datepicker { width: 100%; }
.ord-product-price .ord-price-total { display: flex; justify-content: space-between; align-items: baseline; padding-top: 6px; border-top: 1px dashed #E5E1DA; margin-top: 4px; }
.ord-product-price .ord-price-total span { font-size: 10.5px; font-weight: 800; color: #736C63; text-transform: uppercase; }
.ord-product-price .ord-price-total strong { font-family: 'Sora', sans-serif; font-size: 17px; font-weight: 800; color: #D4620F; }
.ord-product-actions { background: #FFF6F4; border: 1px solid #F3C9C2; display: flex; flex-direction: column; justify-content: center; gap: 6px; padding: 8px; }
.ord-product-actions .btn { padding: 4px 0 !important; font-size: 12px !important; border-radius: 8px !important; }
.ord-product-card .mx-datepicker { width: 100%; }
.ord-product-card .mx-input { height: 32px !important; font-size: 12px !important; border-radius: 8px !important; border: 1.5px solid #E5E1DA !important; padding: 2px 26px 2px 8px !important; }
.ord-hero { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); background: #221F1C; color: #fff; border-radius: 14px; padding: 14px 18px; margin-bottom: 14px; gap: 10px; }
.ord-hero-cell { min-width: 0; }
.ord-hero-cell span { display: block; font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.55); text-transform: uppercase; letter-spacing: 0.05em; }
.ord-hero-cell strong { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 800; color: #fff; }
.ord-hero-cell strong.text-success { color: #7FDACF !important; }
.ord-hero-cell .vs-chip { margin-top: 4px; }
.detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.detail-block { background: #FAF9F7; border: 1px solid #F1EEE9; padding: 16px 18px; border-radius: 14px; }
.detail-block h4 { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 800; margin: 0 0 12px 0; font-family: 'Sora', sans-serif; color: #221F1C; }
.detail-icon { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 8px; font-size: 14px; background: #F1EEE9; color: #736C63; }
.detail-icon.is-dark { background: #221F1C; color: #fff; }
.detail-icon.is-teal { background: #E2F2F0; color: #0F6E6B; }
.detail-icon.is-orange { background: #FFF1E4; color: #D4620F; }
.detail-dl { display: grid; grid-template-columns: 130px 1fr; gap: 6px 12px; margin: 0; font-size: 13px; }
.detail-dl dt { color: #736C63; font-weight: 600; }
.detail-dl dd { margin: 0; font-weight: 700; color: #221F1C; word-break: break-word; }
.loc-flow { display: flex; align-items: center; gap: 10px; }
.loc-endpoint { flex: 1; background: #fff; border: 1px solid #E5E1DA; border-radius: 10px; padding: 10px 12px; min-width: 0; }
.loc-endpoint .loc-label { display: block; font-size: 10px; font-weight: 800; text-transform: uppercase; color: #736C63; }
.loc-endpoint strong { display: block; font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 700; color: #221F1C; margin-top: 2px; word-break: break-word; }
.loc-endpoint small { display: block; font-size: 11px; color: #A59D92; margin-top: 1px; }
.loc-arrow { display: flex; align-items: center; justify-content: center; color: #EE7A22; width: 32px; height: 32px; background: #FFF1E4; border-radius: 50%; flex-shrink: 0; }
.progress-stack { display: flex; flex-direction: column; gap: 10px; }
.progress-line { display: grid; grid-template-columns: 80px 1fr 40px; gap: 10px; align-items: center; font-size: 12.5px; }
.progress-line > span { color: #736C63; font-weight: 600; }
.progress-line > b { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 800; text-align: right; color: #221F1C; }
.progress-bar-wrap { background: #F1EEE9; border-radius: 99px; height: 8px; overflow: hidden; }
.progress-bar-fill { height: 100%; border-radius: 99px; }
.progress-bar-fill.is-ordered   { background: #C98A12; }
.progress-bar-fill.is-scheduled { background: #2B83B5; }
.progress-bar-fill.is-shipped   { background: #D4620F; }
.progress-bar-fill.is-received  { background: #0F6E6B; }
.payment-summary { display: flex; flex-wrap: wrap; background: #F7F5F2; border-radius: 12px; padding: 14px 18px; gap: 20px; }
.payment-summary > div { flex: 1 1 180px; }
.payment-summary span { display: block; font-size: 12px; color: #736C63; font-weight: 600; text-transform: uppercase; }
.payment-summary strong { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: #221F1C; }
.text-success { color: #0F6E6B !important; }
.text-danger  { color: #C93C30 !important; }
@media (max-width: 767px) {
  .detail-grid { grid-template-columns: 1fr; }
  .ord-hero { grid-template-columns: 1fr 1fr; }
  .loc-flow { flex-direction: column; }
  .loc-arrow { transform: rotate(90deg); }
}
</style>

<style>
.order-view .mx-datepicker-popup { z-index: 3000; }
</style>


// WEBPACK FOOTER //
// src/components/TransferOrders/StoreOrderDetailsModal.vue